import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, Clock, Crown, Users } from 'lucide-react';
import { formatCurrency, formatNumber } from '@kardume/services';
import type { PromotionDto } from '@kardume/types';
import { BENEFIT_AUDIENCE_LABELS } from '@kardume/types';
import { Badge } from '@kardume/ui';

/**
 * O que o cliente ganha, na fila de moderação (card #362).
 *
 * ## Por que isto existe
 *
 * Depois do motor de benefícios (card #188), o que o cliente de fato ganha pode
 * não estar em `discountPercent` nenhum: um anúncio com `discountPercent = 0` e
 * benefício "Brinde: sobremesa da casa" chegava ao moderador parecendo um
 * anúncio **sem oferta**. Pior, as restrições que o motor aplica - janela de
 * horário, público, tier de Pass, limite por pessoa - são **condições
 * comerciais** que estavam sendo aprovadas sem ninguém enxergar. "Exclusivo
 * para o plano de topo" é exatamente o tipo de coisa que a moderação existe
 * para olhar.
 *
 * ## Somente leitura, e sem consulta própria
 *
 * O moderador aprova ou recusa; não edita o benefício da loja. E o benefício
 * vem embutido em cada linha da listagem - este componente **não busca nada**,
 * senão a tela faria uma consulta por linha, que é o N+1 que as cláusulas já
 * evitaram.
 *
 * ## A frase vem pronta do core
 *
 * `customerLabel` é resolvido pelo `PromotionBenefitService`, do mesmo template
 * que o app e o site mostram. Montá-la aqui daria uma quarta implementação da
 * mesma frase - e a moderação passaria a aprovar um texto diferente do que o
 * cliente lê.
 */

const Wrapper = styled.div`
  display: grid;
  gap: 0.3rem;
  min-width: 0;
  max-width: 320px;
`;

const Offer = styled.strong`
  font-size: 0.88rem;
  line-height: 1.35;
  color: ${(p) => p.theme.textPrimary};
`;

const Line = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.76rem;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Tags = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-top: 0.1rem;
`;

/** Sem oferta legível é um aviso, não um vazio: é motivo de recusa. */
const Missing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  color: ${(p) => p.theme.warning};
`;

const Legacy = styled.span`
  font-size: 0.76rem;
  color: ${(p) => p.theme.textMuted};
`;

interface Props {
  promotion: PromotionDto;
}

const PromotionBenefitSummary: React.FC<Props> = ({ promotion }) => {
  const { benefit } = promotion;

  if (!benefit) {
    /*
     * Anúncio anterior ao motor, ou cujo lojista ainda não reeditou: o que
     * existe é o percentual da coluna antiga. Mostrar isso - e dizer que é o
     * formato antigo - é melhor que uma célula vazia, que faria o moderador
     * concluir que a oferta sumiu.
     */
    const hasLegacyValue = !!promotion.discountPercent || !!promotion.cashbackPercent;
    if (!hasLegacyValue) {
      return (
        <Missing>
          <AlertTriangle size={14} /> Sem benefício declarado
        </Missing>
      );
    }
    return (
      <Wrapper>
        <Offer>
          {promotion.discountPercent
            ? `${formatNumber(promotion.discountPercent)}% de desconto`
            : `${formatNumber(promotion.cashbackPercent)}% de cashback`}
        </Offer>
        <Legacy>Formato anterior ao motor de benefícios.</Legacy>
      </Wrapper>
    );
  }

  const limits: string[] = [];
  if (benefit.perCustomerLimit) {
    limits.push(
      benefit.perCustomerLimit === 1
        ? 'Um resgate por pessoa'
        : `Até ${formatNumber(benefit.perCustomerLimit)} por pessoa`
    );
  }
  if (promotion.maxRedemptions) {
    limits.push(`Teto de ${formatNumber(promotion.maxRedemptions)} resgates`);
  }
  if (benefit.minPurchaseAmount) {
    limits.push(`Compra mínima de ${formatCurrency(benefit.minPurchaseAmount)}`);
  }
  if (benefit.maxDiscountAmount) {
    limits.push(`Desconto limitado a ${formatCurrency(benefit.maxDiscountAmount)}`);
  }

  return (
    <Wrapper>
      <Offer>{benefit.customerLabel}</Offer>

      {benefit.windowLabel && (
        <Line>
          <Clock size={13} /> {benefit.windowLabel}
        </Line>
      )}

      {benefit.audience !== 'EVERYONE' && (
        <Line>
          <Users size={13} /> {BENEFIT_AUDIENCE_LABELS[benefit.audience]}
          {benefit.audience === 'RETURNING' && benefit.returningMinRedemptions
            ? ` (a partir do ${formatNumber(benefit.returningMinRedemptions)}º resgate)`
            : ''}
        </Line>
      )}

      {limits.length > 0 && <Line>{limits.join(' · ')}</Line>}

      {(benefit.requiredPassPlanName || benefit.flash) && (
        <Tags>
          {/* O tier exigido é a condição comercial mais forte que o motor
              aplica: precisa estar visível ANTES da aprovação. */}
          {benefit.requiredPassPlanName && (
            <Badge tone="primary">
              <Crown size={12} /> Exige Pass {benefit.requiredPassPlanName}
            </Badge>
          )}
          {benefit.flash && <Badge tone="warning">Relâmpago</Badge>}
        </Tags>
      )}
    </Wrapper>
  );
};

export default PromotionBenefitSummary;
